import React, { SyntheticEvent } from 'react';
import Autocomplete, {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
} from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';

type KeywordType = 'helpKeywords' | 'optinKeywords' | 'optoutKeywords';

type KeywordsInputProps = {
  label: string;
  keywordType: KeywordType;
  keywords: string[];
  newKeyword: string;
  setNewKeyword: (value: string) => void;
  onAddKeyword: (keywordType: KeywordType, keywordValue: string, setKeywordValue: (value: string) => void) => void;
  onRemoveKeyword: (keywordType: KeywordType, keywordToRemove: string) => void;
  isReadOnly: boolean;
};

const KeywordsInput: React.FC<KeywordsInputProps> = ({
  label,
  keywordType,
  keywords,
  newKeyword,
  setNewKeyword,
  onAddKeyword,
  onRemoveKeyword,
  isReadOnly,
}) => {
  const handleChange = (
    _event: SyntheticEvent,
    _value: string[],
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<string>,
  ) => {
    if (isReadOnly) {
      return;
    }

    const keywordOption = details?.option?.trim();

    if ((reason === 'createOption' || reason === 'selectOption') && keywordOption) {
      const keyword = keywordOption;
      if (keyword) {
        onAddKeyword(keywordType, keyword, setNewKeyword);
      }
    } else if (reason === 'removeOption' && keywordOption) {
      onRemoveKeyword(keywordType, keywordOption);
    } else if (reason === 'clear') {
      keywords.forEach(keyword => onRemoveKeyword(keywordType, keyword));
    }
  };

  const handleInputChange = (
    _event: SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (isReadOnly) {
      return;
    }

    if (reason === 'input') {
      setNewKeyword(value);
    } else if (reason === 'reset' && value === '') {
      setNewKeyword('');
    } else if (reason === 'clear') {
      setNewKeyword('');
    }
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      options={[]}
      value={keywords}
      inputValue={newKeyword}
      onInputChange={handleInputChange}
      onChange={handleChange}
      disabled={isReadOnly}
      selectOnFocus
      handleHomeEndKeys
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={`${option}-${index}`}
            label={option}
            size="small"
            sx={{
              height: '24px',
              fontSize: '0.75rem',
            }}
          />
        ))
      }
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={isReadOnly ? undefined : 'Type keyword and press Enter'}
        />
      )}
    />
  );
};

export default KeywordsInput;
